// SingleAgencyPage.js
import React from 'react';
import styled from 'styled-components';
import {  useNavigate } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaGlobe, FaArrowLeft, FaStar, FaCheck, FaMapMarkerAlt } from 'react-icons/fa';

const SingleAgencyPage = () => {
  // const { id } = useParams();
  const navigate = useNavigate();

  // Mock data for the single agency
  const agencyData = {
    name: "Wanderlust Adventures",
    logo: "/path-to-logo.png",
    rating: 4.8,
    reviewCount: 256,
    description: "Specializing in crafting unforgettable travel experiences since 1995. Our expert team combines local knowledge with luxury service to create your perfect journey.",
    specialties: ["Luxury Travel", "Adventure Tours", "Honeymoon Packages", "Cultural Expeditions"],
    highlights: [
      "24/7 Customer Support",
      "Personalized Itineraries",
      "Best Price Guarantee",
      "Local Expert Guides"
    ],
    contact: {
      phone: "+1 (555) 123-4567",
      email: "info@wanderlust.com",
      website: "www.wanderlust.com",
      address: "123 Travel Street, Adventure City, AC 12345"
    },
    gallery: [
      "image1.jpg",
      "image2.jpg",
      "image3.jpg"
    ],
    services: [
      {
        title: "Custom Tour Planning",
        description: "Tailored itineraries designed around your preferences and schedule."
      },
      {
        title: "Luxury Accommodations",
        description: "Partnership with premium hotels and resorts worldwide."
      },
      {
        title: "Transportation",
        description: "Private transfers and luxury vehicle rentals."
      }
    ]
  };

  return (
    <StyledSingleAgency>
      <div className="back-button" onClick={() => navigate(-1)}>
        <FaArrowLeft /> Back to Agencies
      </div>

      <div className="agency-header">
        <div className="logo-section">
          <div className="logo-container">
            <img src={agencyData.logo} alt={agencyData.name} />
          </div>
          <div className="rating-container">
            <div className="rating-score">{agencyData.rating}</div>
            <div className="stars">
              {[...Array(5)].map((_, index) => (
                <FaStar
                  key={index}
                  className={index < Math.floor(agencyData.rating) ? 'filled' : ''}
                />
              ))}
            </div>
            <div className="review-count">{agencyData.reviewCount} reviews</div>
          </div>
        </div>
        <div className="header-content">
          <h1>{agencyData.name}</h1>
          <p className="description">{agencyData.description}</p>
          <div className="specialties">
            {agencyData.specialties.map((specialty, index) => (
              <span key={index} className="specialty-tag">{specialty}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="content-grid">
        <div className="main-content">
          <section className="services-section">
            <h2>Our Services</h2>
            <div className="services-grid">
              {agencyData.services.map((service, index) => (
                <div key={index} className="service-card">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="highlights-section">
            <h2>Why Choose Us</h2>
            <div className="highlights-grid">
              {agencyData.highlights.map((highlight, index) => (
                <div key={index} className="highlight-item">
                  <FaCheck className="checkmark" />
                  <span>{highlight}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="gallery-section">
            <h2>Gallery</h2>
            <div className="gallery-grid">
              {agencyData.gallery.map((image, index) => (
                <div key={index} className="gallery-item">
                  <img src={image} alt={`Gallery ${index + 1}`} />
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="contact-sidebar">
          <div className="contact-card">
            <h3>Contact Information</h3>
            <div className="contact-details">
              <a href={`tel:${agencyData.contact.phone}`} className="contact-link">
                <FaPhone /> {agencyData.contact.phone}
              </a>
              <a href={`mailto:${agencyData.contact.email}`} className="contact-link">
                <FaEnvelope /> {agencyData.contact.email}
              </a>
              <a href={`https://${agencyData.contact.website}`} className="contact-link" target="_blank" rel="noopener noreferrer">
                <FaGlobe /> {agencyData.contact.website}
              </a>
              <div className="contact-link address">
                <FaMapMarkerAlt /> {agencyData.contact.address}
              </div>
            </div>

            <div className="enquiry-form">
              <h3>Send Enquiry</h3>
              <form>
                <input type="text" placeholder="Your Name" required />
                <input type="email" placeholder="Your Email" required />
                <input type="tel" placeholder="Your Phone" />
                <textarea placeholder="Your Message" required rows="4"></textarea>
                <button type="submit" className="submit-button">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </aside>
      </div>
    </StyledSingleAgency>
  );
};

const StyledSingleAgency = styled.div`
  padding: 40px 20px;
  max-width: 1200px;
  margin: 0 auto;

  .back-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #2d7ba8;
    cursor: pointer;
    padding: 0.5rem 1rem;
    margin-bottom: 2rem;
    transition: color 0.3s ease;

    &:hover {
      color: #34495e;
    }
  }

  .agency-header {
    background: white;
    border-radius: 20px;
    padding: 2rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    margin-bottom: 2rem;

    .logo-section {
      display: flex;
      align-items: center;
      gap: 2rem;
      margin-bottom: 2rem;

      .logo-container {
        width: 150px;
        height: 150px;
        border-radius: 15px;
        overflow: hidden;
        background: #f8fafc;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 1rem;
        }
      }

      .rating-container {
        .rating-score {
          font-size: 2rem;
          font-weight: 700;
          color: #2d7ba8;
        }

        .stars {
          color: #fbbf24;
          margin: 0.5rem 0;
        }

        .review-count {
          color: #64748b;
        }
      }
    }

    .header-content {
      h1 {
        font-size: 2.5rem;
        color: #1e293b;
        margin-bottom: 1rem;
      }

      .description {
        color: #64748b;
        font-size: 1.1rem;
        line-height: 1.6;
        margin-bottom: 1.5rem;
      }

      .specialties {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;

        .specialty-tag {
          background: #f1f5f9;
          color: #475569;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
        }
      }
    }
  }

  .content-grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 2rem;

    .main-content {
      section {
        background: white;
        border-radius: 20px;
        padding: 2rem;
        margin-bottom: 2rem;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);

        h2 {
          color: #1e293b;
          margin-bottom: 1.5rem;
        }
      }

      .services-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 1.5rem;

        .service-card {
          padding: 1.5rem;
          background: #f8fafc;
          border-radius: 15px;

          h3 {
            color: #2d7ba8;
            margin-bottom: 0.5rem;
          }

          p {
            color: #64748b;
            font-size: 0.95rem;
          }
        }
      }

      .highlights-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;

        .highlight-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;

          .checkmark {
            color: #2d7ba8;
          }
        }
      }

      .gallery-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1rem;

        .gallery-item {
          aspect-ratio: 1;
          border-radius: 10px;
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.3s ease;

            &:hover {
              transform: scale(1.05);
            }
          }
        }
      }
    }

    .contact-sidebar {
      .contact-card {
        background: white;
        border-radius: 20px;
        padding: 2rem;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        position: sticky;
        top: 2rem;

        h3 {
          color: #1e293b;
          margin-bottom: 1.5rem;
        }

        .contact-details {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;

          .contact-link {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            color: #475569;
            text-decoration: none;
            transition: color 0.3s ease;

            &:hover {
              color: #2d7ba8;
            }

            &.address {
              cursor: default;
              &:hover {
                color: #475569;
              }
            }
          }
        }

        .enquiry-form {
          form {
            display: flex;
            flex-direction: column;
            gap: 1rem;

            input, textarea {
              padding: 0.75rem;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              font-size: 0.95rem;

              &:focus {
                outline: none;
                border-color: #2d7ba8;
              }
            }

            .submit-button {
              background: linear-gradient(45deg, #2d7ba8, #34495e);
              color: white;
              border: none;
              padding: 1rem;
              border-radius: 8px;
              font-weight: 500;
              cursor: pointer;
              transition: transform 0.3s ease;

              &:hover {
                transform: translateY(-2px);
              }
            }
          }
        }
      }
    }
  }

  @media (max-width: 1024px) {
    .content-grid {
      grid-template-columns: 1fr;

      .contact-sidebar .contact-card {
        position: static;
      }
    }
  }

  @media (max-width: 768px) {
    .agency-header {
      .logo-section {
        flex-direction: column;
        text-align: center;

        .rating-container {
          margin-top: 1rem;
        }
      }
    }

    .main-content {
      .highlights-grid {
        grid-template-columns: 1fr;
      }

      .gallery-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }
  }

  @media (max-width: 480px) {
    .main-content {
      .gallery-grid {
        grid-template-columns: 1fr;
      }
    }
  }
`;

export default SingleAgencyPage;